<template>
  <div v-if="isProductList" class="py-10">
    <div class="mb-8">
      <category-header :content="headerContent" :current-page="currentPage" />
    </div>
    <product-list
      :product-list="productList"
      :facets-names="category ? category.facets : null"
      :facets="facets"
      :is-page-load="isPageLoad"
      :items-per-page="itemsPerPage"
      :current-page="currentPage"
      :loaded-pages="loadedPages"
      :show-sidebar="true"
      :category="category"
      @filter-changed="loadProductList"
      @sorting-changed="loadProductList"
      @clicked-load-more="loadProductList"
      @list-mounted="loadProductList"
    />
    <div v-if="longDescription && currentPage <= 1" class="container pb-8">
      <div class="row">
        <div class="offset-lg-3 offset-xl-2 col-lg-9 col-xl-10 d-flex justify-content-center">
          <div class="bg-white p-6 p-lg-8 p-xl-12" v-html="longDescription" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CategoryHeader from '@/components/organisms/ShopCommerce/CategoryHeader'
import ProductList from '@/components/organisms/ShopCommerce/ProductList'
import contentMixin from '@/mixins/contentMixin.js'
import { getUrlParameter, updateQueryStringParam } from '@/utils/query'

const getProp = (product, name) =>
  product?.properties?.find((p) => p.name?.toLowerCase() === name.toLowerCase() && p.value !== '')?.value ?? null

export default {
  name: 'ProductListDynamic',
  components: {
    CategoryHeader,
    ProductList
  },
  mixins: [contentMixin],
  data() {
    return {
      isPageLoad: true,
      currentView: 'card',
      currentPage: 1,
      itemsPerPage: 12,
      loadedPages: 0,
      facets: null,
      category: null,
      isLoading: true,
      isPageLoading: false,
      productList: {
        items: []
      },
      klaviyoTracked: false
    }
  },
  computed: {
    culture() {
      return this.$store.getters['site/getCurrentCulture']
    },
    isProductList() {
      return this.$store.getters['content/content/getContent']?.meta.type === 'ContentProductList'
    },
    productListGuid() {
      return this.$store.getters['content/content/getContent']?.meta?.id
    },
    headline() {
      return this.category?.name
    },
    // Reliable category name (from the page payload, available immediately — unlike this.category which loads async).
    categoryName() {
      if (this.category?.name) return this.category.name
      const slug = this.$route.path.split('/').filter(Boolean).pop()
      if (!slug) return null
      const pretty = slug.replace(/-/g, ' ')
      return pretty.charAt(0).toUpperCase() + pretty.slice(1)
    },
    longDescription() {
      return this.category?.properties?.find((prop) => prop.name === 'LongDescription')?.value
    },
    headerImageUrl() {
      const headerBannerValue = this.category?.properties?.find((property) => property.name === 'HeaderBanner')?.value
      return headerBannerValue ? headerBannerValue : this.category?.image?.url
    },
    headerContent() {
      return {
        headline: this.headline,
        rteText: this.category?.description,
        link: null,
        image: this.headerImageUrl && !this.isLoading ? [{ Url: this.headerImageUrl }] : null,
        imageAltText: this.category?.image?.name
      }
    }
  },
  async created() {
    if (this.isProductList) {
      this.category = await this.$repositories.category.byGuid(this.productListGuid, this.culture)
    }

    this.isLoading = false
  },
  methods: {
    async loadProductList(props) {
      if (this.isPageLoading) {
        return
      }
      this.isPageLoading = true
      this.isPageLoad = true

      this.itemsPerPage = this.content.itemsPerPage

      const constructFacets = (facets) => {
        let array = []
        facets.forEach((facet) => {
          array.push(facet.name)
        })
        return array.join('|')
      }

      this.currentPage = props.isReload ? 1 : this.currentPage + 1
      let facetsString = this.productList?.facets ? constructFacets(this.productList.facets) : ''

      if (process.client) {
        if (props.isReload) {
          // Get current page number from URL
          this.currentPage = parseInt(getUrlParameter('page')) || this.currentPage
          this.productList = { ...this.productList, items: [] }
        } else if (this.currentPage > 1) {
          // Update page number in URL
          updateQueryStringParam('page', this.currentPage)
        }
      }

      for (let index = props.isReload ? 1 : this.currentPage; index < this.currentPage + 1; index++) {
        let response = await this.$store.dispatch('products/productList/fetchProductsList', {
          categoryId: this.productListGuid,
          categoryName: this.categoryName,
          culture: this.culture,
          itemsPerPage: this.itemsPerPage,
          currentPage: index,
          facets: facetsString,
          orderBy: props.sorting?.orderBy,
          order: props.sorting?.order,
          filters: props.filters,
          startFromFirstPage: false
        })

        if (this.isPageLoad) {
          this.facets = response.facets
        }

        if (this.isPageLoad && props.isReload) {
          this.loadedPages = index
        }

        // Append to current list
        response.items = [...this.productList?.items, ...response?.items]
        this.productList = response
      }

      // Scroll after the list has loaded
      if (this.isPageLoad && props.isReload) {
        this.goToScrollPosition()
      }

      this.isPageLoad = false
      this.isPageLoading = false

      // Klaviyo tracking
      if (!this.klaviyoTracked && this.currentPage === 1 && this.productList?.items?.length > 0) {
        const eventData = {
          CollectionName: this.categoryName || 'Category',
          ItemNames: this.productList.items.map(p => getProp(p, 'description1') ?? p?.title ?? p?.name ?? p?.displayName ?? p?.productName),
          ProductIDs: this.productList.items.map(p => p.sku || ''),
          URL: typeof window !== 'undefined' ? window.location.pathname : null
        };
        
        const email = this.$store.getters['user/user/getUser']?.email || this.$store.getters['cart/basket/getBasket']?.billingAddress?.email || null
        this.$repositories.klaviyo.track('Viewed Collection', eventData, email, null)
          .catch(e => {
            // Silent fail - don't break the page if Klaviyo tracking fails
          });
        
        this.klaviyoTracked = true;
      }

      // Reset tracking flag on reload
      if (props.isReload) {
        this.klaviyoTracked = false;
      }
    },
    goToScrollPosition() {
      if (process.client) {
        let position = getUrlParameter('position')
        if (position) {
          window.scrollTo({
            top: parseInt(position),
            behavior: 'smooth'
          })
        }
      }
    }
  }
}
</script>
